import React, { useMemo, useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import { Form } from 'formik';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormInputField, {
  FormInputFieldType,
} from '../../common/components/formFields/FormInputField';
import FormSelectField from '../../common/components/formFields/FormSelectField';
import FormDatePickerField from '../../common/components/formFields/FormDatePickerField';
import calendarIcon from '../../assets/img/icons/calendar_month.svg';

import {
  Fuel as VehicleFuels,
  Transmission as VehicleTransmissions,
  BodyType as VehicleBodyTypes,
  Color as VehicleColors,
  VehicleType,
} from '../../constants/Vehicle';

import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import '../../assets/css/componentStyles/FormValidation.css';

const fuelOptions = Object.values(VehicleFuels).map((el) => ({
  value: el,
  label: el,
}));

const transmissionOptions = Object.values(VehicleTransmissions).map((el) => ({
  value: el,
  label: el,
}));

const vehicleTypeOptions = Object.values(VehicleType).map((el) => ({
  value: el,
  label: el,
}));

const bodyTypeOptions = Object.values(VehicleBodyTypes).map((el) => ({
  value: el,
  label: el,
}));

const colorOptions = Object.values(VehicleColors).map((el) => ({
  value: el,
  label: el,
}));

const getDetailsFieldError = (validationErrors, fieldName) => {
  // Maps the component's field name (e.g., 'mileage') to the key in the error object (e.g., 'basicData.mileage')
  const serverKeyMap = {
    makeSelect: 'basicData.makeId',
    modelSelect: 'basicData.modelId',
    derivative: 'basicData.derivative',
    mileage: 'basicData.mileage',
    year: 'basicData.year',
    dateOfFirstRegistration: 'basicData.dateOfFirstRegistration',
    colour: 'basicData.colour',
    carType: 'basicData.carType',
    engineSize: 'basicData.engineSize',
    fuel: 'basicData.fuel',
    doors: 'basicData.doors',
    vin: 'basicData.vin',
    transmission: 'basicData.transmission',
    previousKeepersInTotal: 'basicData.previousKeepersInTotal',
    v5cLogBook: 'basicData.v5cLogbook',
    ulezCompliant: 'basicData.ulezCompliant',
    vehicleType: 'basicData.vehicleType',
    vatQualifying: 'basicData.vatQualifying',
    keys: 'basicData.keys',
    mot: 'basicData.mot',
  };
  const serverKey = serverKeyMap[fieldName];
  // *** FIX: Use optional chaining (?.) to safely access the property ***
  const serverError = validationErrors?.VEHICLE_DETAILS?.[serverKey];
  if (serverError) return serverError;

  // Client-side key mapping (no changes here)
  const clientKeyMap = {
    makeSelect: 'Make',
    modelSelect: 'Model',
    derivative: 'Derivative',
    mileage: 'Millage',
    year: 'Year',
    dateOfFirstRegistration: 'Registration Date',
    colour: 'Colour',
    carType: 'Body Type',
    engineSize: 'Engine Size',
    fuel: 'Fuel',
    doors: 'Doors',
    vin: 'VIN',
    transmission: 'Transmission',
    previousKeepersInTotal: 'Previous Keepers',
    v5cLogBook: 'V5C logbook present',
    ulezCompliant: 'ULEZ compliant',
    vehicleType: 'Vehicle Type',
    vatQualifying: 'VAT Qualifying',
  };
  const clientKey = clientKeyMap[fieldName];
  // *** FIX: Use optional chaining (?.) here as well for safety ***
  const clientError = validationErrors?.VEHICLE_DETAILS?.[clientKey];
  if (clientError) return clientError;

  return null;
};

const shouldShowFieldError = (validationErrors, form, fieldName) => {
  const touched = form.touched?.basicData?.[fieldName];
  const value = form.values?.basicData?.[fieldName];

  // If the field has been touched or has a value, suppress client-side errors
  if (touched || value) return false;

  return !!getDetailsFieldError(validationErrors, fieldName);
};

function AddBasicCarDataForm({
  form,
  makeOptions,
  modelOptions,
  onMakeChange,
  className,
  header = 'Vehicle Details',
  description = 'Add detailed information about the vehicle.',
  validationErrors = {},
  onValidate,
}) {
  const yesNoOptions = useMemo(
    () => [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ],
    [],
  );

  const keysOptions = useMemo(
    () => [
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '2+' },
    ],
    [],
  );

  const formClassName = classNames('text-left', className);

  const fuelsWithoutEngine = useMemo(() => ['Electric'], []);

  const selectedFuel =
    form && form.values && form.values.basicData ? form.values.basicData.fuel : null;

  const isEngineDisabled = useMemo(
    () => fuelsWithoutEngine.includes(selectedFuel?.value),
    [fuelsWithoutEngine, selectedFuel],
  );

  useEffect(() => {
    if (isEngineDisabled && form && form.setFieldValue) {
      const currentEngine = form.values?.basicData?.engineSize;
      if (currentEngine) {
        form.setFieldValue('basicData.engineSize', '');
      }
    }
  }, [isEngineDisabled, form]);

  return (
    <AddVehicleContentWrapper>
      <Form
        className={formClassName}
        role="form"
        aria-label="Vehicle basic data form"
        onBlur={onValidate}
      >
        <SettingsSectionHeader header={header} description={description} />
        <Row className="mt-4">
          <Col>
            <Row className="add-vehicle__basic-data-form-row">
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormSelectField
                  name="basicData.makeSelect"
                  className="form-select-field"
                  options={makeOptions}
                  label="Make"
                  onChangeHandler={onMakeChange}
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select vehicle make"
                  blurInputOnSelect={false}
                />
                {shouldShowFieldError(validationErrors, form, 'makeSelect') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'makeSelect')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3}>
                <FormSelectField
                  name="basicData.modelSelect"
                  className="form-select-field"
                  options={modelOptions}
                  label="Model"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select vehicle model"
                  blurInputOnSelect={false}
                />
                {shouldShowFieldError(validationErrors, form, 'modelSelect') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'modelSelect')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3}>
                <FormSelectField
                  name="basicData.vehicleType"
                  className="form-select-field"
                  options={vehicleTypeOptions}
                  label="Vehicle Type"
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select vehicle type"
                  required
                />
                {shouldShowFieldError(validationErrors, form, 'vehicleType') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'vehicleType')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3}>
                <FormSelectField
                  name="basicData.vatQualifying"
                  className="form-select-field"
                  options={yesNoOptions}
                  label="VAT Qualifying"
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select vat qualifying"
                  required
                />
                {shouldShowFieldError(validationErrors, form, 'vatQualifying') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'vatQualifying')}
                  </div>
                )}
              </Col>
            </Row>
            <Row className="add-vehicle__basic-data-form-row mt-3">
              <Col md={12} lg={12}>
                <FormInputField
                  name="basicData.derivative"
                  type={FormInputFieldType.TEXT}
                  label="Derivative"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Enter vehicle derivative"
                  maxLength={150}
                  lengthCounter
                />
                {shouldShowFieldError(validationErrors, form, 'derivative') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'derivative')}
                  </div>
                )}
              </Col>
            </Row>
            <Row className="add-vehicle__basic-data-form-row mt-3">
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormInputField
                  name="basicData.mileage"
                  type={FormInputFieldType.NUMBER}
                  label="Mileage"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Enter vehicle mileage"
                  min="0"
                />
                {shouldShowFieldError(validationErrors, form, 'mileage') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'mileage')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormInputField
                  name="basicData.year"
                  type={FormInputFieldType.TEXT}
                  icon={calendarIcon}
                  iconClassName="calendar-icon"
                  label="Year"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select vehicle year"
                />
                {shouldShowFieldError(validationErrors, form, 'year') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'year')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormInputField
                  type={FormInputFieldType.TEXT}
                  label="Date of first registration"
                  name="basicData.dateOfFirstRegistration"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Enter date of first registration"
                  placeholder="dd/mm/yyyy"
                  maxLength={10}
                />
                {shouldShowFieldError(validationErrors, form, 'dateOfFirstRegistration') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'dateOfFirstRegistration')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3}>
                <FormSelectField
                  name="basicData.colour"
                  className="form-select-field"
                  options={colorOptions}
                  label="Colour"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Enter vehicle colour"
                  blurInputOnSelect={false}
                />
                {shouldShowFieldError(validationErrors, form, 'colour') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'colour')}
                  </div>
                )}
              </Col>
            </Row>
            <Row className="add-vehicle__basic-data-form-row mt-3 ">
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormSelectField
                  name="basicData.carType"
                  className="form-select-field"
                  options={bodyTypeOptions}
                  label="Body type"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Enter vehicle body type"
                  blurInputOnSelect={false}
                />
                {shouldShowFieldError(validationErrors, form, 'carType') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'carType')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormInputField
                  name="basicData.engineSize"
                  type={FormInputFieldType.TEXT}
                  label="Engine size"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Enter engine size"
                  disabled={isEngineDisabled}
                />
                {shouldShowFieldError(validationErrors, form, 'engineSize') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'engineSize')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormSelectField
                  name="basicData.fuel"
                  className="form-select-field"
                  options={fuelOptions}
                  label="Fuel"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select fuel type"
                />
                {shouldShowFieldError(validationErrors, form, 'fuel') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'fuel')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3}>
                <FormInputField
                  name="basicData.doors"
                  className="form-select-field"
                  type={FormInputFieldType.NUMBER}
                  label="Doors"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select number of doors"
                  min={1}
                  max={5}
                />
                {form.values?.basicData?.doors &&
                  (Number(form.values.basicData.doors) < 1 ||
                    Number(form.values.basicData.doors) > 5) && (
                    <div className="field-error-message">
                      Number of doors must be between 1 and 5.
                    </div>
                  )}
                {shouldShowFieldError(validationErrors, form, 'doors') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'doors')}
                  </div>
                )}
              </Col>
            </Row>
            <Row className="add-vehicle__basic-data-form-row mt-3">
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormInputField
                  name="basicData.vin"
                  type={FormInputFieldType.TEXT}
                  label="VIN / Chassis No."
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Enter VIN or chassis number"
                  upperCase
                />
                {shouldShowFieldError(validationErrors, form, 'vin') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'vin')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormSelectField
                  name="basicData.transmission"
                  className="form-select-field"
                  options={transmissionOptions}
                  label="Transmission"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select transmission type"
                  blurInputOnSelect={false}
                />
                {shouldShowFieldError(validationErrors, form, 'transmission') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'transmission')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormInputField
                  name="basicData.previousKeepersInTotal"
                  type={FormInputFieldType.TEXT}
                  label="Previous Keepers"
                  required
                  labelVariant="bordered"
                  form={form}
                  aria-label="Enter previous keepers"
                />
                {shouldShowFieldError(validationErrors, form, 'previousKeepersInTotal') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'previousKeepersInTotal')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3}>
                <FormDatePickerField
                  label="MOT"
                  name="basicData.mot"
                  icon={calendarIcon}
                  iconClassName="calendar-icon z-index-1"
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select MOT expiry date"
                />
                {shouldShowFieldError(validationErrors, form, 'mot') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'mot')}
                  </div>
                )}
              </Col>
            </Row>
            <Row className="add-vehicle__basic-data-form-row mt-3">
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormSelectField
                  label="V5C logbook present"
                  name="basicData.v5cLogBook"
                  options={yesNoOptions}
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select if V5C logbook is present"
                  required
                  blurInputOnSelect={false}
                />
                {shouldShowFieldError(validationErrors, form, 'v5cLogBook') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'v5cLogBook')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3} className="mb-3 mb-md-0">
                <FormSelectField
                  label="ULEZ compliant"
                  name="basicData.ulezCompliant"
                  options={yesNoOptions}
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select if vehicle is ULEZ compliant"
                  required
                  blurInputOnSelect={false}
                />
                {shouldShowFieldError(validationErrors, form, 'ulezCompliant') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'ulezCompliant')}
                  </div>
                )}
              </Col>
              <Col xs={12} md={4} lg={3}>
                <FormSelectField
                  label="Number of keys included"
                  name="basicData.keys"
                  options={keysOptions}
                  labelVariant="bordered"
                  form={form}
                  aria-label="Select number of keys included"
                  blurInputOnSelect={false}
                />
                {shouldShowFieldError(validationErrors, form, 'keys') && (
                  <div className="field-error-message">
                    {getDetailsFieldError(validationErrors, 'keys')}
                  </div>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </AddVehicleContentWrapper>
  );
}

AddBasicCarDataForm.propTypes = {
  form: PropTypes.shape({
    values: PropTypes.object,
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }).isRequired,
  makeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  modelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onMakeChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  header: PropTypes.string,
  description: PropTypes.string,
  validationErrors: PropTypes.object,
  onValidate: PropTypes.func,
};

AddBasicCarDataForm.defaultProps = {
  className: '',
  header: 'Vehicle Details',
  description: 'Add detailed information about the vehicle.',
  validationErrors: {},
  onValidate: () => {},
};

export default AddBasicCarDataForm;
